/**
 * Data + actions for the pipeline Review queue (SE-5499).
 *
 * Two sharp edges of the SE-5498 schema are handled here:
 *
 *  1. `pipelineReviewQueue` filters by a SINGLE `status` enum (server default
 *     PENDING) with no "ALL" value. AC4 needs pending AND resolved items in one
 *     list, and a resolved item must stay visible — so we run the query THREE
 *     times (PENDING / ACCEPTED / DECLINED) and merge client-side. This is the
 *     brief's sanctioned deterministic fallback; it does not rely on the
 *     unverified `status: null` = "all" behaviour.
 *
 *  2. `AcceptPipelineReviewRequestInput.reason` is REQUIRED, but the product
 *     decision is a SILENT accept (no reason prompt). We auto-supply the
 *     `PIPELINE_REVIEW_ACCEPT_REASON` constant and pass the request's
 *     `requestedOutcome` through so a terminal target keeps its outcome.
 *
 * After a decision, `refetchQueries: ["PipelineReviewQueue"]` refreshes all
 * three status instances so the item moves from the pending group to the
 * resolved group cleanly while staying visible.
 * @module features/player-kanban/hooks/usePipelineReviewQueue
 */

import { useCallback, useMemo, useState } from "react";

import {
  PipelineReviewSource,
  PipelineReviewStatus,
  useAcceptPipelineReviewRequestMutation,
  useDeclinePipelineReviewRequestMutation,
  usePipelineReviewQueueQuery,
} from "@/generated/graphql";
import { AnalyticsTracker } from "@/features/analytics";
import { useToastNotification } from "@/hooks/useToastNotification";

import { resolveReviewAgeHours } from "../utils/reviewQueueRows";
import {
  MESSAGES,
  PIPELINE_REVIEW_ACCEPT_REASON,
  REVIEW_QUEUE_PAGE_SIZE,
} from "../constants";
import {
  type ReviewQueueRow,
  type ReviewQueueSourceFilter,
  buildReviewQueueRows,
  filterReviewQueueRowsBySource,
  sortReviewQueueRows,
} from "../utils/reviewQueueRows";

/** Operation name whose refetch refreshes all three status instances. */
const REVIEW_QUEUE_OP = "PipelineReviewQueue";

/** Arguments for {@link usePipelineReviewQueue}. */
export interface UsePipelineReviewQueueArgs {
  /** Board scope; null/undefined = whole-org queue. */
  readonly kanbanBoardId?: string | null;
  /** Skip all queries (e.g. while the drawer is closed). */
  readonly skip?: boolean;
}

/** How many rows each producer source contributes, before any filtering. */
export type ReviewQueueSourceCounts = Readonly<
  Record<PipelineReviewSource, number>
>;

/** Public API of {@link usePipelineReviewQueue}. */
export interface UsePipelineReviewQueueResult {
  /** Rows after the active source filter + sort (pending first, newest-first). */
  readonly rows: readonly ReviewQueueRow[];
  /**
   * SE-6554: rows per source across the WHOLE queue, unaffected by the active
   * filter — the type-filter pills label themselves with these, so switching
   * filters must not renumber (or hide) the pills you are switching between.
   */
  readonly sourceCounts: ReviewQueueSourceCounts;
  /** SE-6554: total unfiltered rows — the "All · N" pill's count. */
  readonly totalCount: number;
  /**
   * SE-6554: unfiltered rows still awaiting a decision — the header's count
   * badge. Deliberately NOT `totalCount`: the badge answers "how many things
   * need me", and an accepted row that stays visible (AC4) needs nobody.
   */
  readonly pendingCount: number;
  /** Whether any of the status queries is loading. */
  readonly loading: boolean;
  /** Whether any of the status queries errored. */
  readonly isError: boolean;
  /** Active client-side source filter. */
  readonly sourceFilter: ReviewQueueSourceFilter;
  /** Set the client-side source filter. */
  readonly setSourceFilter: (filter: ReviewQueueSourceFilter) => void;
  /** Ids with an in-flight accept/decline. */
  readonly decidingIds: ReadonlySet<string>;
  /** Accept a request — SILENT (auto reason), passing through requestedOutcome. */
  readonly accept: (row: ReviewQueueRow) => Promise<void>;
  /** Decline a request (no reason). */
  readonly decline: (row: ReviewQueueRow) => Promise<void>;
}

/**
 * Fetch + merge + act on the pipeline Review queue.
 * @param args - {@link UsePipelineReviewQueueArgs}
 * @returns The {@link UsePipelineReviewQueueResult}.
 */
const usePipelineReviewQueue = (
  args: UsePipelineReviewQueueArgs = {}
): UsePipelineReviewQueueResult => {
  const { kanbanBoardId, skip = false } = args;
  const toast = useToastNotification();

  const [sourceFilter, setSourceFilter] =
    useState<ReviewQueueSourceFilter>("all");
  const [decidingIds, setDecidingIds] = useState<ReadonlySet<string>>(
    new Set()
  );

  const boardArg = kanbanBoardId ?? undefined;

  const pending = usePipelineReviewQueueQuery({
    variables: {
      status: PipelineReviewStatus.Pending,
      kanbanBoardId: boardArg,
      first: REVIEW_QUEUE_PAGE_SIZE,
    },
    skip,
  });
  const accepted = usePipelineReviewQueueQuery({
    variables: {
      status: PipelineReviewStatus.Accepted,
      kanbanBoardId: boardArg,
      first: REVIEW_QUEUE_PAGE_SIZE,
    },
    skip,
  });
  const declined = usePipelineReviewQueueQuery({
    variables: {
      status: PipelineReviewStatus.Declined,
      kanbanBoardId: boardArg,
      first: REVIEW_QUEUE_PAGE_SIZE,
    },
    skip,
  });

  const [acceptMutation] = useAcceptPipelineReviewRequestMutation();
  const [declineMutation] = useDeclinePipelineReviewRequestMutation();

  /**
   * Every row the three status queries returned, before the source filter.
   * Kept separate from `rows` so the filter pills can count what the filter
   * would REVEAL, not what it currently shows.
   */
  const allRows = useMemo<readonly ReviewQueueRow[]>(() => {
    const requests = [pending.data, accepted.data, declined.data]
      .flatMap(data => data?.pipelineReviewQueue.edges ?? [])
      .map(edge => edge.node);
    return buildReviewQueueRows(requests);
  }, [pending.data, accepted.data, declined.data]);

  const rows = useMemo<readonly ReviewQueueRow[]>(
    () =>
      sortReviewQueueRows(filterReviewQueueRowsBySource(allRows, sourceFilter)),
    [allRows, sourceFilter]
  );

  const sourceCounts = useMemo<ReviewQueueSourceCounts>(
    () =>
      allRows.reduce(
        (acc, row) => ({ ...acc, [row.source]: acc[row.source] + 1 }),
        {
          [PipelineReviewSource.AutomationRule]: 0,
          [PipelineReviewSource.Manual]: 0,
          [PipelineReviewSource.ScoutFlag]: 0,
        } as ReviewQueueSourceCounts
      ),
    [allRows]
  );

  const pendingCount = useMemo(
    () => allRows.filter(row => row.isPending).length,
    [allRows]
  );

  const markDeciding = useCallback((id: string, active: boolean) => {
    setDecidingIds(prev =>
      active
        ? new Set([...prev, id])
        : new Set([...prev].filter(existing => existing !== id))
    );
  }, []);

  const accept = useCallback(
    async (row: ReviewQueueRow) => {
      markDeciding(row.id, true);
      try {
        await acceptMutation({
          variables: {
            input: {
              reviewRequestId: row.id,
              // Required field, but the accept is SILENT — no prompt (AC3).
              reason: PIPELINE_REVIEW_ACCEPT_REASON,
              // Pass the proposed outcome through for a terminal target.
              ...(row.requestedOutcome
                ? { outcome: row.requestedOutcome }
                : {}),
            },
          },
          refetchQueries: [REVIEW_QUEUE_OP],
          awaitRefetchQueries: true,
        });
        // SE-5508 `review_item_resolved`. Inside the `try`, after the awaited
        // mutation resolved — the `catch` below swallows the failure without
        // rethrowing, so an emit placed after the try/catch would report every
        // failed decision as a resolution.
        void AnalyticsTracker.pipeline.logReviewItemResolved({
          decision: "accept",
          type: row.source,
          age_hours: resolveReviewAgeHours(row.createdAt, Date.now()),
          board_id: row.kanbanBoardId,
        });
        toast.show({ action: "success", description: "Request accepted" });
      } catch {
        toast.show({
          action: "danger",
          description: MESSAGES.ERROR.GENERIC,
        });
      } finally {
        markDeciding(row.id, false);
      }
    },
    [acceptMutation, markDeciding, toast]
  );

  const decline = useCallback(
    async (row: ReviewQueueRow) => {
      markDeciding(row.id, true);
      try {
        await declineMutation({
          variables: { input: { reviewRequestId: row.id } },
          refetchQueries: [REVIEW_QUEUE_OP],
          awaitRefetchQueries: true,
        });
        // SE-5508 `review_item_resolved`. Same placement rationale as `accept`.
        void AnalyticsTracker.pipeline.logReviewItemResolved({
          decision: "decline",
          type: row.source,
          age_hours: resolveReviewAgeHours(row.createdAt, Date.now()),
          board_id: row.kanbanBoardId,
        });
        toast.show({ action: "success", description: "Request declined" });
      } catch {
        toast.show({
          action: "danger",
          description: MESSAGES.ERROR.GENERIC,
        });
      } finally {
        markDeciding(row.id, false);
      }
    },
    [declineMutation, markDeciding, toast]
  );

  const loading = pending.loading || accepted.loading || declined.loading;
  const isError = Boolean(pending.error || accepted.error || declined.error);

  return useMemo(
    () => ({
      rows,
      sourceCounts,
      totalCount: allRows.length,
      pendingCount,
      loading,
      isError,
      sourceFilter,
      setSourceFilter,
      decidingIds,
      accept,
      decline,
    }),
    [
      rows,
      sourceCounts,
      allRows.length,
      pendingCount,
      loading,
      isError,
      sourceFilter,
      decidingIds,
      accept,
      decline,
    ]
  );
};

export default usePipelineReviewQueue;
